import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowLeft } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { Section } from '../components/ui/Section';
import { CTASection } from '../components/sections/CTASection';
import { useServices } from '../hooks/useServices';
import { useProjects } from '../hooks/useProjects';
const cardColors = ['bg-blue-50', 'bg-amber-50', 'bg-green-50', 'bg-purple-50', 'bg-rose-50', 'bg-cyan-50'];
// Detect if a URL points to a video file
function isVideo(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase().split('?')[0];
  return ['.mp4', '.webm', '.mov', '.ogg'].some(ext => lower.endsWith(ext));
}
// Render image or video based on URL
function MediaElement({
  src,
  alt,
  className,
  autoPlay = true
}: {
  src: string;
  alt: string;
  className?: string;
  autoPlay?: boolean;
}) {
  if (isVideo(src)) {
    return <video src={src} className={className} autoPlay={autoPlay} loop muted playsInline data-id="element-946" />;
  }
  return <img src={src} alt={alt} className={className} data-id="element-947" />;
}
export function ServiceDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    services,
    loading: servicesLoading
  } = useServices();
  const {
    projects,
    loading: projectsLoading
  } = useProjects();
  const service = services.find(s => s.slug === slug);
  usePageMeta({
    title: service?.title || 'Service',
    description: service?.description?.slice(0, 160) || 'Explore this service offered by Belo Digital.',
    image: service?.image
  });
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (servicesLoading) {
    return <main className="pt-32 pb-20 min-h-screen flex items-center justify-center" data-id="element-948">
        <div className="animate-pulse flex flex-col items-center" data-id="element-949">
          <div className="w-12 h-12 border-4 border-[#00316B] border-t-transparent rounded-full animate-spin mb-4" data-id="element-950" />
          <p className="text-gray-500" data-id="element-951">Loading service details...</p>
        </div>
      </main>;
  }
  if (!service) {
    return <main className="pt-32 pb-20 min-h-screen" data-id="element-952">
        <Section background="default" data-id="element-953">
          <div className="text-center max-w-2xl mx-auto" data-id="element-954">
            <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-955">
              Service Not Found
            </h1>
            <p className="text-gray-600 mb-8" data-id="element-956">
              The service you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/services" className="inline-flex items-center text-[#FF6600] font-medium hover:text-[#CC5200] transition-colors" data-id="element-957">
              <ArrowLeft className="w-4 h-4 mr-2" data-id="element-958" />
              Back to Services
            </Link>
          </div>
        </Section>
      </main>;
  }
  // Find projects that match this service's title
  const relatedProjects = projects.filter(p => p.category === service.title);
  return <main data-id="element-959">
      {/* Full-bleed Hero Banner */}
      <div className="relative w-full h-[280px] md:h-[340px] lg:h-[380px] bg-[#00316B]" data-id="element-960">
        {service.image && <MediaElement src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-961" />}
        {/* Blue overlay */}
        <div className="absolute inset-0 bg-[#00316B]/50" data-id="element-962" />

        {/* Content on top of image */}
        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14" data-id="element-963">
          <Link to="/services" className="inline-flex items-center text-sm font-medium text-white/70 hover:text-white transition-colors mb-6 w-fit" data-id="element-964">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-965" />
            All Services
          </Link>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-white leading-tight" data-id="element-966">
            {service.title}
          </motion.h1>
        </div>
      </div>

      {/* Description Section */}
      <Section background="default" data-id="element-967">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="prose prose-lg text-gray-600 leading-relaxed whitespace-pre-wrap w-full max-w-none" data-id="element-968">
          {service.description}
        </motion.div>
      </Section>

      {/* What We Offer — Specialities from Contentful */}
      {service.specialities && service.specialities.length > 0 && <Section background="white" data-id="element-969">
          <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-12" data-id="element-970">
            What we offer
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-971">
            {service.specialities.map((spec, index) => {
          const placeholderImages = ['https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1522542550221-31fd19575a2d?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=500&fit=crop'];
          const imgSrc = spec.image || placeholderImages[index % placeholderImages.length];
          return <motion.div key={spec.id} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100" data-id="element-972">
                  <div className="aspect-[8/5] bg-gray-100 overflow-hidden" data-id="element-973">
                    <MediaElement src={imgSrc} alt={spec.title} className="w-full h-full object-cover" data-id="element-974" />
                  </div>
                  <div className="border-l-4 border-[#FF6600] p-6" data-id="element-975">
                    <h3 className="text-lg font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-976">
                      {spec.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm" data-id="element-977">
                      {spec.description}
                    </p>
                  </div>
                </motion.div>;
        })}
          </div>
        </Section>}

      {/* Related Projects Section */}
      <Section background="white" data-id="element-978">
        <div className="mb-12" data-id="element-979">
          <h2 className="text-3xl font-bold font-heading text-[#0C0C0C]" data-id="element-980">
            Selected Work
          </h2>
          <p className="text-gray-600 mt-2" data-id="element-981">
            Recent projects in {service.title.toLowerCase()}
          </p>
        </div>

        {projectsLoading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse" data-id="element-982">
            {[1, 2].map(i => <div key={i} data-id="element-983">
                <div className="aspect-[4/3] rounded-xl bg-gray-200 mb-6" data-id="element-984" />
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" data-id="element-985" />
                <div className="h-4 bg-gray-100 rounded w-1/2" data-id="element-986" />
              </div>)}
          </div> : relatedProjects.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-987">
            {relatedProjects.map((project, index) => {
          const bgColor = cardColors[index % cardColors.length];
          const href = project.live_url || '#';
          return <motion.article key={project.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="group cursor-pointer" data-id="element-988">
                  <a href={href} target={project.live_url ? '_blank' : '_self'} rel="noopener noreferrer" className="block" data-id="element-989">
                    <div className={`aspect-[4/3] rounded-xl overflow-hidden mb-6 ${project.cover_image ? 'bg-gray-100' : bgColor} relative`} data-id="element-990">
                      {project.cover_image ? <MediaElement src={project.cover_image} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-991" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-992">
                          <div className="w-3/4 h-3/4 bg-white/50 rounded-lg shadow-lg flex items-center justify-center" data-id="element-993">
                            <span className="text-gray-400 text-sm" data-id="element-994">
                              Project Preview
                            </span>
                          </div>
                        </div>}
                      <div className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md" data-id="element-995">
                        <ArrowUpRight className="w-5 h-5 text-[#0C0C0C]" data-id="element-996" />
                      </div>
                    </div>

                    <div className="mb-3" data-id="element-997">
                      <h3 className="text-2xl font-bold font-heading text-[#0C0C0C] group-hover:text-[#00316B] transition-colors" data-id="element-998">
                        {project.title}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-999">
                        {project.client_name ? `Client: ${project.client_name}` : project.category}
                      </p>
                    </div>

                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2" data-id="element-1000">
                      {project.description}
                    </p>
                  </a>
                </motion.article>;
        })}
          </div> : <div className="bg-gray-50 rounded-2xl p-12 text-center" data-id="element-1001">
            <p className="text-gray-500" data-id="element-1002">
              No projects showcased in this category yet. Check back soon!
            </p>
          </div>}
      </Section>

      <CTASection data-id="element-1003" />
    </main>;
}